import { useCallback, useEffect, useState } from "react";
import {
  ANIMATION_CONFIG,
  HINT_CONFIG,
  LEVEL_CONFIG,
  getRandomAnimal,
  playButtonSound,
  playCompleteSound,
  playCorrectSound,
  playWrongSound,
} from "../../constants";
import { useGame } from "../../context";
import "./DecompositionGame.css";

// 生成問題
const generateQuestion = (level) => {
  // 根據等級決定最大數值
  const config =
    LEVEL_CONFIG.DECOMPOSITION_RANGES[Math.min(level, 10)] ||
    LEVEL_CONFIG.DECOMPOSITION_RANGES[10];
  const maxNum = config.max;

  // 隨機選擇動物
  const animal = getRandomAnimal();

  // 隨機決定問題類型（加法或減法）
  const isAddition = Math.random() > 0.5;

  let num1, answer, total;

  if (isAddition) {
    // 加法型：A + ? = C
    total = Math.floor(Math.random() * (maxNum - 2)) + 3; // 3 到 maxNum
    num1 = Math.floor(Math.random() * (total - 1)) + 1; // 1 到 total-1
    answer = total - num1;

    return {
      type: "addition",
      num1,
      answer,
      total,
      animal,
      display: `${num1} + ? = ${total}`,
      question: `幫${animal.name}算一算：${num1} 加上多少等於 ${total}？`,
    };
  } else {
    // 減法型：C - A = ?
    total = Math.floor(Math.random() * (maxNum - 2)) + 3;
    num1 = Math.floor(Math.random() * (total - 1)) + 1;
    answer = total - num1;

    return {
      type: "subtraction",
      num1,
      answer,
      total,
      animal,
      display: `${total} - ${num1} = ?`,
      question: `幫${animal.name}算一算：${total} 減去 ${num1} 等於多少？`,
    };
  }
};

// 湊十法提示生成
const getMakeTenHint = (question) => {
  const { type, num1, total, answer } = question;

  if (type === "addition") {
    if (total > 10 && num1 < 10) {
      const toTen = 10 - num1;
      const remaining = answer - toTen;
      return {
        method: "makeTen",
        steps: [
          `先湊十：${num1} + ${toTen} = 10`,
          `再加剩餘：10 + ${remaining} = ${total}`,
          `所以答案是：${toTen} + ${remaining} = ${answer}`,
        ],
      };
    }
  } else {
    if (total > 10 && total - num1 >= 0) {
      if (num1 <= total - 10) {
        return {
          method: "directSubtract",
          steps: [
            `${total} - ${num1} = ${answer}`,
            `從${total}個${question.animal.itemEmoji}拿走${num1}個`,
            `還剩${answer}個${question.animal.itemEmoji}`,
          ],
        };
      }
    }
  }

  return {
    method: "counting",
    steps: [
      `用數數的方法`,
      type === "addition" ? `從${num1}數到${total}` : `從${total}倒數${num1}個`,
      `答案是 ${answer}`,
    ],
  };
};

// 十格圖組件 - 加法有不同背景色，減法顯示交叉，加法的答案格顯示紅邊框
const TenFrame = ({
  count,
  animal,
  maxDisplay = 20,
  isSubtraction = false,
  subtractNum = 0,
  answerCount = 0, // 加法時，需要顯示紅邊框的答案格數量
}) => {
  const firstTen = Math.min(count, 10);
  const secondTen = Math.max(0, count - 10);

  // 對於減法，從第一個開始交叉
  // 先交叉第一格，再交叉第二格
  const crossedInFirst = isSubtraction ? Math.min(subtractNum, firstTen) : 0;
  const crossedInSecond = isSubtraction
    ? Math.max(0, subtractNum - firstTen)
    : 0;

  // 對於加法，計算需要紅邊框的格數（答案的格數）
  // 答案格從 num1 位置開始，共 answerCount 個
  const num1 = count - answerCount; // 已知的數量
  const answerStartInFirst = Math.min(num1, 10); // 第一格中答案開始的位置
  const answerInFirst = !isSubtraction
    ? Math.min(answerCount, firstTen - answerStartInFirst)
    : 0;
  const answerInSecond = !isSubtraction
    ? Math.max(0, answerCount - answerInFirst)
    : 0;

  return (
    <div
      className={`ten-frame-container ${
        isSubtraction ? "subtraction-mode" : "addition-mode"
      }`}
    >
      <div
        className={`ten-frame ${isSubtraction ? "subtraction" : "addition"}`}
      >
        <div className="ten-frame-grid">
          {Array(10)
            .fill(null)
            .map((_, index) => {
              const isFilled = index < firstTen;
              // 減法時，從第一個開始交叉 (index 0, 1, 2...)
              const isCrossed =
                isSubtraction && isFilled && index < crossedInFirst;
              // 加法時，答案格顯示紅邊框（從 num1 開始的格子）
              const isAnswerCell =
                !isSubtraction && isFilled && index >= answerStartInFirst;
              return (
                <div
                  key={`first-${index}`}
                  className={`ten-frame-cell ${isFilled ? "filled" : ""} ${
                    isCrossed ? "crossed" : ""
                  } ${isAnswerCell ? "answer-cell" : ""} ${
                    isSubtraction ? "subtraction-cell" : "addition-cell"
                  }`}
                >
                  {isFilled && (
                    <>
                      <span className="cell-emoji">{animal.emoji}</span>
                      {isCrossed && <span className="cross-mark">✕</span>}
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {maxDisplay > 10 && (
        <div
          className={`ten-frame ${isSubtraction ? "subtraction" : "addition"}`}
        >
          <div className="ten-frame-grid">
            {Array(10)
              .fill(null)
              .map((_, index) => {
                const isFilled = index < secondTen;
                // 減法時，第二格從第一個開始交叉
                const isCrossed =
                  isSubtraction && isFilled && index < crossedInSecond;
                // 加法時，第二格的答案格（如果第一格不夠）
                const isAnswerCell =
                  !isSubtraction &&
                  isFilled &&
                  (answerStartInFirst >= 10
                    ? index < answerInSecond
                    : index < answerCount - answerInFirst);
                return (
                  <div
                    key={`second-${index}`}
                    className={`ten-frame-cell ${isFilled ? "filled" : ""} ${
                      isCrossed ? "crossed" : ""
                    } ${isAnswerCell ? "answer-cell" : ""} ${
                      isSubtraction ? "subtraction-cell" : "addition-cell"
                    }`}
                  >
                    {isFilled && (
                      <>
                        <span className="cell-emoji">{animal.emoji}</span>
                        {isCrossed && <span className="cross-mark">✕</span>}
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div className="ten-frame-count">
        {isSubtraction ? (
          <>
            總共: {count} 個 {animal.itemEmoji}，減去 {subtractNum} 個
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

// 提示組件
const HintDisplay = ({ hint, onClose }) => {
  return (
    <div className="hint-overlay" onClick={onClose}>
      <div className="hint-card" onClick={(e) => e.stopPropagation()}>
        <div className="hint-header">
          <span className="hint-icon">💡</span>
          <span className="hint-title">提示</span>
        </div>
        <div className="hint-content">
          {hint.steps.map((step, index) => (
            <div key={index} className="hint-step">
              <span className="step-number">{index + 1}</span>
              <span className="step-text">{step}</span>
            </div>
          ))}
        </div>
        <button className="hint-close-btn" onClick={onClose}>
          我知道了 ✨
        </button>
      </div>
    </div>
  );
};

// 生成3個相似選項
const generateChoices = (correctAnswer, maxNum) => {
  const choices = new Set([correctAnswer]);

  // 生成相似的錯誤選項
  const generateSimilar = () => {
    const offsets = [-2, -1, 1, 2, 3, -3];
    for (const offset of offsets) {
      const similar = correctAnswer + offset;
      if (similar >= 0 && similar <= maxNum && !choices.has(similar)) {
        choices.add(similar);
        if (choices.size >= 3) break;
      }
    }

    // 如果還不夠，添加隨機數
    while (choices.size < 3) {
      const random = Math.floor(Math.random() * (maxNum + 1));
      if (!choices.has(random)) {
        choices.add(random);
      }
    }
  };

  generateSimilar();

  // 轉換為數組並隨機排序
  return Array.from(choices).sort(() => Math.random() - 0.5);
};

const DecompositionGame = ({ onBack }) => {
  const { state, actions } = useGame();
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResult, setShowResult] = useState(null); // 'correct' | 'wrong' | null
  const [showHint, setShowHint] = useState(false);
  const [hint, setHint] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [choices, setChoices] = useState([]);

  const currentLevel = state.gameProgress.decomposition.level;
  const gameProgress = state.gameProgress.decomposition;

  // 初始化遊戲
  useEffect(() => {
    actions.startGame("decomposition");
    generateNewQuestion();

    return () => {
      actions.endGame();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 等級提升音效
  useEffect(() => {
    if (state.showLevelUp) {
      playCompleteSound();
    }
  }, [state.showLevelUp]);

  // 生成新問題
  const generateNewQuestion = useCallback(() => {
    const newQuestion = generateQuestion(currentLevel);
    setQuestion(newQuestion);
    actions.setQuestion(newQuestion);
    setUserAnswer("");
    setShowResult(null);
    setShowHint(false);
    setHintsUsed(0);

    // 生成3個選項
    const config =
      LEVEL_CONFIG.DECOMPOSITION_RANGES[Math.min(currentLevel, 10)] ||
      LEVEL_CONFIG.DECOMPOSITION_RANGES[10];
    setChoices(generateChoices(newQuestion.answer, config.max));
  }, [currentLevel, actions]);

  // 處理答案提交
  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (!userAnswer.trim()) return;
  //   const numAnswer = parseInt(userAnswer, 10);
  //   if (isNaN(numAnswer)) {
  //     actions.showFeedback("請輸入數字！", "warning");
  //     return;
  //   }
  //   checkAnswer(numAnswer);
  // };

  // 處理選擇答案
  const handleChoiceSelect = (choice) => {
    if (showResult) return; // 防止重複選擇
    playButtonSound();
    setUserAnswer(choice.toString());
    checkAnswer(choice);
  };

  // 檢查答案
  const checkAnswer = (numAnswer) => {
    if (numAnswer === question.answer) {
      // 答對
      playCorrectSound();
      setShowResult("correct");
      actions.answerCorrect("decomposition", hintsUsed > 0);

      // 延遲生成新問題
      setTimeout(() => {
        generateNewQuestion();
      }, ANIMATION_CONFIG.SUCCESS_DELAY);
    } else {
      // 答錯
      playWrongSound();
      setShowResult("wrong");
      actions.answerWrong("decomposition");

      // 短暫顯示錯誤後重置
      setTimeout(() => {
        setShowResult(null);
        setUserAnswer("");
      }, ANIMATION_CONFIG.WRONG_SHAKE_DURATION);
    }
  };

  // 處理提示請求
  const handleHint = () => {
    playButtonSound();
    if (hintsUsed >= HINT_CONFIG.MAX_HINTS_PER_QUESTION) {
      actions.showFeedback("這題已經沒有更多提示了！", "info");
      return;
    }

    const newHint = getMakeTenHint(question);
    setHint(newHint);
    setShowHint(true);
    setHintsUsed((prev) => prev + 1);
    actions.useHint();
  };

  // 處理跳過
  const handleSkip = () => {
    playButtonSound();
    actions.showFeedback(`正確答案是 ${question.answer}`, "info");
    setTimeout(() => {
      generateNewQuestion();
    }, 2000);
  };

  if (!question) {
    return (
      <div className="game-loading">
        <span className="loading-emoji">🔄</span>
        <span>載入中...</span>
      </div>
    );
  }

  return (
    <div
      className="decomposition-game"
      style={{ backgroundColor: question.animal.bgColor }}
    >
      {/* 遊戲頭部 */}
      <div className="game-header">
        <button className="back-button" onClick={onBack}>
          <span>←</span> 返回
        </button>

        <div className="game-info">
          <div className="info-item">
            <span className="info-icon">🎯</span>
            <span className="info-value">等級 {currentLevel}</span>
          </div>
          <div className="info-item">
            <span className="info-icon">⭐</span>
            <span className="info-value">{state.totalStars}</span>
          </div>
          <div className="info-item">
            <span className="info-icon">🏆</span>
            <span className="info-value">{state.totalScore}</span>
          </div>
        </div>
      </div>

      {/* 進度條 */}
      <div className="level-progress">
        <div className="progress-label">
          升級進度：{gameProgress.correctCount % 5} / 5
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(gameProgress.correctCount % 5) * 20}%` }}
          />
        </div>
      </div>

      {/* 主遊戲區域 */}
      <div className="game-main">
        {/* 動物和問題 */}
        <div className="question-section">
          <div className="animal-display">
            <span className="animal-emoji large">{question.animal.emoji}</span>
            <div className="animal-speech">{question.question}</div>
          </div>

          <div className={`question-display ${showResult} ${question.type}`}>
            <span className="question-text">{question.display}</span>
          </div>
        </div>

        {/* 十格圖視覺輔助 */}
        <TenFrame
          count={question.total}
          animal={question.animal}
          maxDisplay={currentLevel >= 7 ? 20 : 10}
          isSubtraction={question.type === "subtraction"}
          subtractNum={question.type === "subtraction" ? question.num1 : 0}
          answerCount={question.type === "addition" ? question.answer : 0}
        />

        {/* 選擇答案區 */}
        <div className="answer-section">
          <div className="choice-buttons">
            {choices.map((choice, index) => (
              <button
                key={index}
                type="button"
                className={`choice-button ${
                  userAnswer === choice.toString() ? showResult || "" : ""
                }`}
                onClick={() => handleChoiceSelect(choice)}
                disabled={showResult === "correct"}
              >
                {choice}
              </button>
            ))}
          </div>

          <div className="action-buttons">
            <button
              type="button"
              className="hint-button"
              onClick={handleHint}
              disabled={hintsUsed >= HINT_CONFIG.MAX_HINTS_PER_QUESTION}
            >
              💡 提示 ({HINT_CONFIG.MAX_HINTS_PER_QUESTION - hintsUsed}次)
            </button>
            <button type="button" className="skip-button" onClick={handleSkip}>
              ⏭️ 跳過
            </button>
          </div>
        </div>

        {/* 結果反饋 */}
        {showResult === "correct" && (
          <div className="result-overlay correct">
            <div className="result-content">
              <span className="result-emoji">🎉</span>
              <span className="result-text">答對了！</span>
              <span className="result-score">+{hintsUsed > 0 ? 7 : 10} 分</span>
            </div>
          </div>
        )}

        {showResult === "wrong" && (
          <div className="result-message wrong">
            <span>再試試看！💪</span>
          </div>
        )}
      </div>

      {/* 提示彈窗 */}
      {showHint && hint && (
        <HintDisplay hint={hint} onClose={() => setShowHint(false)} />
      )}

      {/* 升級動畫 */}
      {state.showLevelUp && (
        <div className="level-up-overlay">
          <div className="level-up-content">
            <span className="level-up-emoji">🎊</span>
            <span className="level-up-text">等級提升！</span>
            <span className="level-up-level">Level {currentLevel}</span>
            <button onClick={actions.hideLevelUp} className="level-up-btn">
              太棒了！
            </button>
          </div>
        </div>
      )}

      {/* 連續答對提示 */}
      {state.streak >= 3 && (
        <div className="streak-indicator">🔥 連續答對 {state.streak} 題！</div>
      )}
    </div>
  );
};

export default DecompositionGame;
